import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TariffTypeEnum } from '@/graphql/generated';

/**
 * Hook that provides localized lookup functionality for TariffTypeEnum values
 *
 * @example
 * ```tsx
 * const { getTariffTypeTitle, tariffTypeOptions } = useTariffTypeLookup();
 *
 * // Get display title for an enum value
 * const title = getTariffTypeTitle(TariffTypeEnum.Tiered); // "Tiered Pricing"
 *
 * // Use in form dropdowns
 * <Select options={tariffTypeOptions} />
 * ```
 */
export const useTariffTypeLookup = () => {
	const { t } = useTranslation('tariffs');

	const tariffTypeMap = useMemo(() => ({
		[TariffTypeEnum.Tiered]: {
			title: t('tariffType.tiered.title'),
			shortTitle: t('tariffType.tiered.shortTitle'),
			description: t('tariffType.tiered.description')
		},
		[TariffTypeEnum.FlatRate]: {
			title: t('tariffType.flatRate.title'),
			shortTitle: t('tariffType.flatRate.shortTitle'),
			description: t('tariffType.flatRate.description')
		}
	}), [t]);

	/**
	 * Get the display title for a tariff type enum value
	 * @param tariffType - The TariffTypeEnum value
	 * @param useShortTitle - Whether to use the short version of the title
	 * @returns Localized display title or 'Unknown Type' if not found
	 */
	const getTariffTypeTitle = (
		tariffType?: TariffTypeEnum | string | null,
		useShortTitle = false
	): string => {
		if (!tariffType) return t('tariffType.notSet', 'Not set');

		const typeInfo = tariffTypeMap[tariffType as TariffTypeEnum];
		if (!typeInfo) return t('tariffType.unknown', 'Unknown Type');

		return useShortTitle ? typeInfo.shortTitle : typeInfo.title;
	};

	/**
	 * Get the description for a tariff type enum value
	 * @param tariffType - The TariffTypeEnum value
	 * @returns Localized description or empty string if not found
	 */
	const getTariffTypeDescription = (
		tariffType?: TariffTypeEnum | string | null
	): string => {
		if (!tariffType) return '';

		const typeInfo = tariffTypeMap[tariffType as TariffTypeEnum];
		return typeInfo?.description ?? t('tariffType.unknownDescription', '');
	};

	/**
	 * Check if a tariff type is tiered pricing
	 * @param tariffType - The TariffTypeEnum value
	 * @returns true if the tariff type is tiered
	 */
	const isTieredTariff = (tariffType?: TariffTypeEnum | string | null): boolean => {
		return tariffType === TariffTypeEnum.Tiered || tariffType === 'TIERED';
	};

	/**
	 * Check if a tariff type is flat rate pricing
	 * @param tariffType - The TariffTypeEnum value
	 * @returns true if the tariff type is flat rate
	 */
	const isFlatRateTariff = (tariffType?: TariffTypeEnum | string | null): boolean => {
		return tariffType === TariffTypeEnum.FlatRate || tariffType === 'FLAT_RATE';
	};

	/**
	 * Options array for use in form dropdowns and filters
	 * Includes empty option for "All Types" scenarios
	 */
	const tariffTypeOptions = useMemo(() => [
		{ value: '', label: t('tariffType.allTypes', 'All Tariff Types'), description: '' },
		{
			value: TariffTypeEnum.Tiered,
			label: tariffTypeMap[TariffTypeEnum.Tiered].title,
			description: tariffTypeMap[TariffTypeEnum.Tiered].description
		},
		{
			value: TariffTypeEnum.FlatRate,
			label: tariffTypeMap[TariffTypeEnum.FlatRate].title,
			description: tariffTypeMap[TariffTypeEnum.FlatRate].description
		}
	], [tariffTypeMap]);

	/**
	 * Options array for form dropdowns (without "All Types" option)
	 * Use this for create/edit forms where a selection is required
	 */
	const tariffTypeFormOptions = useMemo(() => [
		{
			value: TariffTypeEnum.Tiered,
			label: tariffTypeMap[TariffTypeEnum.Tiered].title,
			description: tariffTypeMap[TariffTypeEnum.Tiered].description
		},
		{
			value: TariffTypeEnum.FlatRate,
			label: tariffTypeMap[TariffTypeEnum.FlatRate].title,
			description: tariffTypeMap[TariffTypeEnum.FlatRate].description
		}
	], [tariffTypeMap]);

	return {
		getTariffTypeTitle,
		getTariffTypeDescription,
		isTieredTariff,
		isFlatRateTariff,
		tariffTypeOptions,      // For filters with "All Types"
		tariffTypeFormOptions,  // For forms without "All Types"
		tariffTypeMap          // Raw map for advanced use cases
	};
};

export default useTariffTypeLookup;
